/**
 * Phase 21 — Setup Diagnostics panel.
 *
 * Renders the response from ``GET /api/v1/onboarding/diagnostics`` as a
 * table of status pills. Each check row exposes a "Fix" button that
 * either deep-links to the appropriate settings/onboarding route or
 * lists the env vars the operator must set (informational — we never
 * mutate the environment from the browser).
 *
 * Props:
 *   embedded — when true, skips the outer Card + heading (Onboarding
 *              inlines the panel into its shell).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, AlertTriangle, XCircle, MinusCircle,
  RefreshCw, Wrench, ExternalLink, Loader2,
} from "lucide-react";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const STATUS_META = {
  ok: {
    icon: CheckCircle2, cls: "text-emerald-600",
    pill: "bg-emerald-100 text-emerald-800 border-emerald-300",
  },
  warn: {
    icon: AlertTriangle, cls: "text-amber-600",
    pill: "bg-amber-100 text-amber-800 border-amber-300",
  },
  error: {
    icon: XCircle, cls: "text-rose-600",
    pill: "bg-rose-100 text-rose-800 border-rose-300",
  },
  skipped: {
    icon: MinusCircle, cls: "text-slate-400",
    pill: "bg-slate-100 text-slate-600 border-slate-300",
  },
};

function CheckRow({ check, onFix }) {
  const meta = STATUS_META[check.status] || STATUS_META.skipped;
  const Icon = meta.icon;
  const detail = typeof check.detail === "string"
    ? check.detail : check.detail?.reason;
  return (
    <div
      className="flex items-start justify-between border rounded-md px-3 py-2 gap-3"
      data-testid={`diagnostic-row-${check.name}`}
    >
      <div className="flex items-start gap-3 min-w-0">
        <Icon className={`size-4 mt-0.5 shrink-0 ${meta.cls}`} />
        <div className="min-w-0">
          <div className="text-sm font-medium">{check.name}</div>
          {detail && (
            <div className="text-xs text-muted-foreground truncate max-w-[420px]"
                    data-testid={`diagnostic-detail-${check.name}`}>
              {detail}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant="outline"
                  data-testid={`diagnostic-status-${check.name}`}
                  className={`font-mono text-[10px] ${meta.pill}`}>
          {check.status}
        </Badge>
        {check.fix_action && (
          <Button
            variant="ghost" size="sm"
            data-testid={`diagnostic-fix-${check.name}`}
            onClick={() => onFix(check)}
            className="gap-1"
          >
            <Wrench className="size-3" />
            Fix
          </Button>
        )}
      </div>
    </div>
  );
}

export default function DiagnosticsPanel({ embedded = false }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/onboarding/diagnostics",
        { skipPermissionToast: true });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load diagnostics");
    } finally {
      setLoading(false);
    }
  }
  async function runFull() {
    setRunning(true);
    try {
      const r = await api.post("/onboarding/diagnostics/run-full");
      setData(r.data);
      toast.success(`Ran ${r.data.checks.length} checks in ${r.data.total_latency_ms}ms`);
    } catch (e) {
      toast.error("Full diagnostics run failed");
    } finally {
      setRunning(false);
    }
  }
  useEffect(() => { load(); }, []);

  function applyFix(check) {
    const fa = check.fix_action;
    if (!fa) return;
    if (fa.kind === "open_settings" || fa.kind === "open_route") {
      nav(fa.route);
    } else if (fa.kind === "set_env") {
      const label = (fa.vars || []).join(", ");
      toast.info(`Set env vars: ${label}`, {
        description: "Add these to /app/backend/.env and restart backend.",
        duration: 8000,
      });
    }
  }

  const checks = data?.checks || [];
  const checklist = data?.go_live_checklist || [];

  const body = (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Overall:</span>
          <Badge
            variant="outline"
            data-testid="diagnostics-overall-status"
            className={`font-mono ${
              STATUS_META[data?.status]?.pill
                || STATUS_META.skipped.pill}`}
          >
            {loading ? "loading" : data?.status || "—"}
          </Badge>
          {data?.total_latency_ms != null && (
            <span className="text-muted-foreground font-mono">
              {data.total_latency_ms}ms
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            size="sm" variant="outline"
            onClick={load} disabled={loading}
            data-testid="diagnostics-refresh"
          >
            <RefreshCw className={`size-3 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={runFull} disabled={running}
            data-testid="diagnostics-run-full"
          >
            {running && <Loader2 className="size-3 mr-1 animate-spin" />}
            Test everything
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {loading && !data && (
          <div className="text-sm text-muted-foreground">Loading…</div>
        )}
        {checks.map((c) => (
          <CheckRow key={c.name} check={c} onFix={applyFix} />
        ))}
      </div>

      {checklist.length > 0 && (
        <div className="mt-6 border rounded-md p-3 bg-muted/30"
                data-testid="go-live-checklist">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
            Go-Live Prerequisites
            <Badge
              variant="outline"
              data-testid="go-live-ready-badge"
              className={`font-mono ${
                checklist.every((c) => c.satisfied)
                    ? STATUS_META.ok.pill
                    : STATUS_META.warn.pill}`}
            >
              {checklist.filter((c) => c.satisfied).length} / {checklist.length}
            </Badge>
          </div>
          <ul className="space-y-1">
            {checklist.map((c) => {
              const Icon = c.satisfied
                ? CheckCircle2 : (c.required ? XCircle : AlertTriangle);
              const cls = c.satisfied
                ? "text-emerald-600"
                : (c.required ? "text-rose-600" : "text-amber-600");
              return (
                <li key={c.name}
                       className="text-xs flex items-center gap-2"
                       data-testid={`go-live-row-${c.name}`}>
                  <Icon className={`size-3 ${cls}`} />
                  <span className="font-medium">{c.name}</span>
                  {c.required
                    ? <Badge variant="outline"
                                  className="text-[9px] px-1">required</Badge>
                    : <Badge variant="outline"
                                  className="text-[9px] px-1 text-muted-foreground">optional</Badge>}
                  {!c.satisfied && c.fix_action && (
                    <Button size="sm" variant="link" className="h-4 p-0 text-xs"
                              onClick={() => applyFix(c)}
                              data-testid={`go-live-fix-${c.name}`}>
                      Fix <ExternalLink className="size-2 ml-0.5" />
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );

  if (embedded) {
    return <div data-testid="diagnostics-panel">{body}</div>;
  }
  return (
    <Card data-testid="diagnostics-panel">
      <CardHeader>
        <CardTitle className="text-base">Setup diagnostics</CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
